import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Alert } from '../../design/primitives/Alert';

/**
 * O que uma lista percorrida por CURSOR pode estar dizendo — e nada alem disso.
 *
 * "Nenhuma colisao na fila" e uma afirmacao sobre o **servidor**, e so quem viu
 * a resposta inteira tem como faze-la. A fila de colisao dizia essa frase em
 * tres situacoes que nao tem nada em comum: quando a fila estava mesmo vazia,
 * enquanto a primeira pagina vinha, e quando a leitura falhou. As duas ultimas
 * sao mentiras, e a terceira e a pior de todas — o Super Admin e a UNICA pessoa
 * que alcanca essa fila, e a frase que ele recebe no lugar do erro e justamente
 * a que garante que ele nao vai procurar. Duas aulas sobrepostas na grade de um
 * professor ficam de pe, e ninguem sabe.
 *
 * Com cursor ha uma quarta situacao que nao existe em lista de pagina unica: a
 * varredura ainda em curso. A lista incompleta ja tem itens e mesmo assim nao e
 * a lista — dizer "vazia" nela seria a mesma mentira com mais passos.
 *
 * A correcao recusada foi acrescentar `&& !isError && !hasNextPage` a cada
 * `length === 0` das telas. Sao quatro listas com cursor nesta fatia, quatro
 * oportunidades de esquecer uma das duas condicoes, e o esquecimento nao quebra
 * nada visivel — ele so mente, que e o modo de falha mais caro que existe aqui.
 * O que esta escrito abaixo e mais forte, e e a mesma forma que `panelState`
 * (dashboard/PanelSection.tsx) ja deu a este problema na home: o vazio existe
 * apenas como valor que `settledList` produz **depois** de olhar erro e cursor,
 * e a marca que este modulo nao exporta impede qualquer tela de montar
 * `{ kind: 'empty' }` a mao.
 */
declare const producedBySettledList: unique symbol;

interface Sealed {
  readonly [producedBySettledList]: true;
}

export type SettledList<T> =
  | (Sealed & { readonly kind: 'loading' })
  | (Sealed & { readonly kind: 'failed' })
  | (Sealed & { readonly kind: 'empty' })
  | (Sealed & {
      readonly kind: 'ready';
      readonly items: readonly [T, ...T[]];
      /**
       * A varredura parou antes do fim por causa de uma pagina que recusou. O
       * que esta na tela e um pedaco, e a tela precisa dizer isso: lista parcial
       * apresentada como completa e o mesmo defeito do vazio que mente, so que
       * mais dificil de perceber.
       */
      readonly truncatedByFailure: boolean;
    });

/** O recorte de `useInfiniteQuery` que decide o estado, e so ele. */
export interface CursorSnapshot<T> {
  readonly isError: boolean;
  readonly hasNextPage: boolean;
  /** `undefined` enquanto nenhuma resposta chegou. */
  readonly items: readonly T[] | undefined;
}

/**
 * Deriva o estado da lista. A ORDEM DOS TESTES E O CONTEUDO DA REGRA:
 *
 * 1. Itens primeiro. O que ja chegou vale a pena mostrar mesmo com a varredura
 *    interrompida — esconder a pagina 1 porque a 2 falhou tira alcance de quem
 *    tinha o que resolver. Mas `truncatedByFailure` viaja junto, para a tela nao
 *    apresentar o pedaco como se fosse o todo.
 * 2. Erro antes de pendente. Com `retry: false` em `app/queryClient.ts`, a
 *    consulta que falhou nao esta mais carregando, e girar sobre uma falha e a
 *    mesma mentira de antes, so que mais lenta.
 * 3. `items === undefined` OU `hasNextPage` sao carregamento. O segundo e o que
 *    nao existe em lista sem cursor: ha resposta, ha itens, e ainda assim a
 *    lista nao fechou.
 * 4. So aqui o vazio e uma afirmacao verdadeira sobre o servidor.
 *
 * `isError` cobre tambem a pagina que falha NO MEIO: `isFetchNextPageError` e
 * derivado como `isError && direcao === 'forward'`
 * (`query-core/infiniteQueryObserver.js`), ou seja, a falha de qualquer pagina
 * poe a consulta inteira em erro, com os dados ja recebidos preservados.
 */
export function settledList<T>(snapshot: CursorSnapshot<T>): SettledList<T> {
  const items = snapshot.items ?? [];
  const [first, ...rest] = items;
  if (first !== undefined) {
    return seal<T>({ kind: 'ready', items: [first, ...rest], truncatedByFailure: snapshot.isError });
  }
  if (snapshot.isError) return seal<T>({ kind: 'failed' });
  if (snapshot.items === undefined || snapshot.hasNextPage) return seal<T>({ kind: 'loading' });
  return seal<T>({ kind: 'empty' });
}

/**
 * A marca so pode ser posta aqui, e este modulo nao a exporta: e o que impede
 * uma tela de escrever `{ kind: 'empty' }` a mao e reabrir o buraco por fora.
 */
type Unsealed<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'empty' }
  | {
      readonly kind: 'ready';
      readonly items: readonly [T, ...T[]];
      readonly truncatedByFailure: boolean;
    };

function seal<T>(state: Unsealed<T>): SettledList<T> {
  return state as SettledList<T>;
}

/**
 * Tudo o que a lista mostra, incluindo o que ela mostra quando nao e lista.
 *
 * O `switch` sem `default` e o que garante que um estado novo no tipo quebre a
 * compilacao aqui, em vez de cair calado num ramo vazio e reabrir o buraco pelo
 * outro lado.
 */
export function SettledListView<T>({
  state,
  empty,
  emptyClassName,
  children,
}: {
  readonly state: SettledList<T>;
  readonly empty: string;
  readonly emptyClassName?: string;
  readonly children: (items: readonly [T, ...T[]]) => ReactNode;
}) {
  const { t } = useTranslation();

  switch (state.kind) {
    case 'loading':
      return <p role="status">{t('app.loading')}</p>;
    case 'failed':
      return <Alert tone="error">{t('error.loadFailed')}</Alert>;
    case 'empty':
      return <p className={emptyClassName}>{empty}</p>;
    case 'ready':
      return (
        <>
          {/*
            O alerta vem ANTES da lista de proposito: quem le de cima para baixo
            precisa saber que o que vem a seguir esta incompleto antes de contar
            os itens e concluir que aquilo e tudo.
          */}
          {state.truncatedByFailure ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
          {children(state.items)}
        </>
      );
  }
}
